import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NfcIcon from '@mui/icons-material/Nfc';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { getCurrentWindow } from '@tauri-apps/api/window';
import { checkPermissions, openAppSettings, requestPermissions } from '@tauri-apps/plugin-barcode-scanner';
import { useCallback, useEffect, useState } from 'react';

import { useHasNext } from '@ui/Hooks/useHasNext';
import { useNfc } from '@ui/Utils/scan/nfc/useNfc';

import { useCameraStore } from 'kerberus-components/src/Components/Scan/QR/cameraStore';

import { NfcScan } from './NfcScan';

type ViewState = 'hub' | 'nfc';

export default function ScanView() {
    const hasNext = useHasNext();
    const [view, setView] = useState<ViewState>('hub');
    const { reset, scan } = useCameraStore();
    const [cameraPermission, setCameraPermission] = useState<'unknown' | 'granted' | 'denied' | 'permanently-denied'>(
        'unknown',
    );
    const [checkingCamPerm, setCheckingCamPerm] = useState(false);

    const ensureCameraPermission = useCallback(async () => {
        try {
            setCheckingCamPerm(true);
            const status = await checkPermissions();
            if (status === 'granted') {
                setCameraPermission('granted');
                return true;
            }
            const req = await requestPermissions();
            if (req === 'granted') {
                setCameraPermission('granted');
                return true;
            }
            // Heuristic: if after request still not granted assume possibly permanently denied on Android if twice denied
            setCameraPermission(status === 'denied' && req === 'denied' ? 'permanently-denied' : 'denied');
            return false;
        } catch (error) {
            console.error('Camera permission check failed', error);
            setCameraPermission('denied');
            return false;
        } finally {
            setCheckingCamPerm(false);
        }
    }, []);
    const nfc = useNfc();
    const { cancel, checkSupport, clear } = nfc;

    useEffect(() => {
        void checkSupport();
    }, [checkSupport]);

    const goHub = useCallback(() => {
        cancel();
        clear();
        setView('hub');
    }, [cancel, clear]);

    const appWindow = getCurrentWindow();

    // Listen for window focus changes (e.g., when Quick Settings panel is opened/closed)
    useEffect(() => {
        const unlistenFocus = appWindow.onFocusChanged(({ payload: focused }) => {
            if (!focused) {
                console.log('App lost focus - Quick Settings panel opened?');
            } else {
                console.log('App regained focus - Quick Settings panel closed?');
                void checkSupport();
            }
        });

        return () => {
            void unlistenFocus.then((fn) => fn());
        };
    }, [appWindow, cancel, reset, checkSupport]);

    // Refresh camera permission when the app window becomes focused or page visible again
    useEffect(() => {
        let running = true;
        const passiveRecheck = async () => {
            try {
                const status = await checkPermissions();
                if (!running) {
                    return;
                }
                if (status === 'granted') {
                    setCameraPermission('granted');
                } else if (cameraPermission === 'granted') {
                    // Edge case: permission revoked in settings
                    setCameraPermission('denied');
                }
            } catch (error) {
                console.error('Passive camera permission recheck failed', error);
            }
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                void passiveRecheck();
            }
        };
        const onFocus = () => {
            void passiveRecheck();
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        return () => {
            running = false;
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
        };
        // Intentionally exclude cameraPermission to avoid resetting heuristic churn on quick transitions
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!hasNext) {
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                    p: 3,
                    textAlign: 'center',
                }}
            >
                <Typography variant="h5">Restricted Feature</Typography>
                <Typography
                    sx={{
                        color: 'text.secondary',
                        maxWidth: 400,
                    }}
                    variant="body1"
                >
                    Scanning (QR / NFC) is available only for enabled users.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                p: 2,
            }}
        >
            {view === 'hub' ? (
                <Fade in>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            gap: 4,
                            justifyContent: 'center',
                            minHeight: '60vh',
                            textAlign: 'center',
                        }}
                    >
                        <Stack
                            sx={{
                                alignItems: 'center',
                                gap: 2,
                                width: '100%',
                            }}
                        >
                            <Typography sx={{ textAlign: 'center' }} variant="h4">
                                Scan
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    maxWidth: 340,
                                }}
                                variant="h6"
                            >
                                Choose how you want to scan.
                            </Typography>
                        </Stack>
                        <Stack
                            direction="row"
                            sx={{
                                gap: 2,
                                width: '100%',
                            }}
                        >
                            <Button
                                disabled={checkingCamPerm}
                                fullWidth
                                onClick={() => {
                                    void (async () => {
                                        const ok = await ensureCameraPermission();
                                        if (!ok) {
                                            if (cameraPermission === 'permanently-denied') {
                                                void openAppSettings().catch(console.error);
                                            }
                                            return;
                                        }
                                        reset();
                                        await scan();
                                    })().catch(console.error);
                                }}
                                size="large"
                                startIcon={<QrCodeScannerIcon />}
                                sx={{ fontSize: '1rem', py: 2 }}
                                variant="outlined"
                            >
                                {cameraPermission === 'permanently-denied' ? 'Open Settings' : 'QR'}
                            </Button>
                            <Button
                                disabled={nfc.supported === false}
                                fullWidth
                                onClick={() => setView('nfc')}
                                size="large"
                                startIcon={<NfcIcon />}
                                sx={{ fontSize: '1rem', py: 2 }}
                                variant="outlined"
                            >
                                NFC
                            </Button>
                        </Stack>
                        {nfc.supported === false ? (
                            <Typography color="warning" sx={{ maxWidth: 360 }} variant="body2">
                                NFC is not enabled.
                            </Typography>
                        ) : null}
                        {cameraPermission === 'denied' ? (
                            <Typography
                                sx={{
                                    color: 'warning.main',
                                    maxWidth: 360,
                                }}
                                variant="body2"
                            >
                                Camera permission denied. Tap QR again to re-ask.
                            </Typography>
                        ) : null}
                        {cameraPermission === 'permanently-denied' ? (
                            <Stack
                                sx={{
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography color="error" sx={{ maxWidth: 360 }} variant="body2">
                                    Camera permission permanently denied. Use settings to enable the camera for this
                                    app.
                                </Typography>
                                <Button
                                    onClick={() => {
                                        void openAppSettings().catch(console.error);
                                    }}
                                    size="small"
                                >
                                    Open App Settings
                                </Button>
                            </Stack>
                        ) : null}
                    </Box>
                </Fade>
            ) : null}
            {view === 'nfc' ? (
                <Fade in>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 1,
                            width: '100%',
                        }}
                    >
                        <NfcScan
                            cancel={nfc.cancel}
                            checkSupport={nfc.checkSupport}
                            isAllowed={nfc.isAllowed}
                            lastError={nfc.lastError}
                            lastTag={nfc.lastTag}
                            lastUid={nfc.lastUid}
                            lastUrl={nfc.lastUrl}
                            redirectUrl={nfc.redirectUrl}
                            scanning={nfc.scanning}
                            scanTag={nfc.scanTag}
                            supported={nfc.supported}
                        />
                        <Stack
                            direction="row"
                            sx={{
                                gap: 1,
                            }}
                        >
                            <Button onClick={goHub} startIcon={<ArrowBackIcon fontSize="inherit" />} variant="outlined">
                                Back
                            </Button>
                        </Stack>
                    </Box>
                </Fade>
            ) : null}
        </Box>
    );
}
